'use memo'

import React from 'react'
import { useField } from 'formik'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import PlayerIdChip from 'src/features/profile/player/setupModal/components/PlayerIdChip'

interface Props {
  /**
   * Explicit id, for flows where the value isn't a form field (the profile Edit
   * Player modal reads it off the API response). Omit to fall back to the
   * `salesforceId` Formik field used by the roster form.
   */
  salesforceId?: string | null
}

/**
 * Read-only SALESFORCE ID chip, stacked under the PLAYER ID chip in a form's
 * Personal Information header. Canlan-only, and only once the player actually
 * has a Salesforce ID — renders null otherwise.
 *
 * When falling back to the Formik field it reads the SAVED value
 * (`initialValue`), not the live input: the chip is an identity/copy affordance,
 * so it shouldn't echo keystrokes from the Salesforce ID field in Roster
 * Information. It picks up a new value after the form re-baselines on save.
 */
const SalesforceIdChip: React.FC<Props> = ({ salesforceId }) => {
  // Hook order is stable — the field is always subscribed, its value only used
  // when no explicit id was passed.
  const [, meta] = useField<string>('salesforceId')
  const id = (salesforceId ?? meta.initialValue ?? '').trim()

  if (!isCanlan || id === '') {
    return null
  }

  return (
    <PlayerIdChip playerId={id} label='SALESFORCE ID' idName='Salesforce ID' />
  )
}

export default SalesforceIdChip
